import type { MouseEvent, ReactElement, ReactNode } from "react";
import { Children, cloneElement } from "react";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import type { DefaultTheme } from "simplydo/react/styled";
import { cx, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Icon, type LucideIconData } from "simplydo/react/ui/icon";

import { Slot } from "../utils/Slot";
import { type UiColor, resolveUiColor } from "../utils/colors";
import type { MenuItemProps } from "./Menu.types";
function resolveItemColor(
  theme: DefaultTheme,
  opts: {
    color?: UiColor;
    disabled?: boolean;
    header?: boolean;
    active?: boolean;
  },
): string {
  if (opts.disabled) return "rgba(40, 40, 40, 0.3)";
  if (opts.color) {
    return resolveUiColor(theme, opts.color) || "rgba(0, 0, 0, 0.95)";
  }
  if (opts.header || opts.active) return "rgba(0, 0, 0, 0.95)";
  return "rgba(0, 0, 0, 0.87)";
}

export type MenuItemStyleProps = {
  $colorValue?: string;
  $fitted?: boolean | "horizontally" | "vertically";
};

export const StyledMenuItem = styled.a<MenuItemStyleProps>`
  position: relative;
  display: flex;

  align-items: center;
  gap: 0.5em;
  padding: ${({ $fitted }) => {
    if ($fitted === true) return "0";
    if ($fitted === "horizontally") return "0.4em 0";
    if ($fitted === "vertically") return "0 0.4em";
    return "0.4em 1em";
  }};
  line-height: 1;
  text-decoration: none;
  cursor: pointer;
  color: ${({ $colorValue }) => $colorValue || "rgba(0, 0, 0, 0.87)"};
  background: transparent;
  font-weight: 400;
  opacity: 1;
  pointer-events: auto;
  user-select: none;
  box-sizing: border-box;
  margin: 0;
  border: none;
  outline: none;
  border-radius: 4px;

  &.is-active:not(.is-header) {
    background: rgba(0, 0, 0, 0.05);
  }

  &.is-active,
  &.is-header {
    font-weight: 700;
  }

  &.is-disabled,
  &.is-header {
    cursor: default;
  }

  &.is-disabled {
    opacity: 0.45;
    pointer-events: none;
  }

  &.is-position-right {
    margin-left: auto;
  }

  &.is-position-left {
    margin-right: auto;
  }

  &.is-icon {
    justify-content: center;
    > i.icon,
    > svg.icon {
      margin: 0;
    }
  }

  &:hover {
    color: rgba(0, 0, 0, 0.95);
  }

  &:hover:not(.is-header):not(.is-disabled):not(.is-active) {
    background: rgba(0, 0, 0, 0.03);
  }
`;

const MenuItemComponent = (props: MenuItemProps): ReactElement => {
  const theme = useTheme();
  const {
    asChild,
    active,
    content,
    children,
    className,
    style,
    color,
    disabled,
    fitted,
    header,
    icon,
    name,
    link,
    position,
    to,
    href,
    onClick,
    ...rest
  } = props;

  const slotChild = asChild
    ? (Children.only(children) as ReactElement<{ children?: ReactNode }>)
    : null;
  const body =
    content !== undefined
      ? content
      : asChild
        ? slotChild?.props.children
        : children !== undefined
          ? children
          : name !== undefined
            ? name
            : null;

  const iconOnly = icon === true && body == null;

  const renderedIcon =
    icon && icon !== true ? <Icon icon={icon as LucideIconData} /> : null;

  const handleClick = onClick
    ? (event: MouseEvent<HTMLElement>) => {
        if (disabled) {
          event.preventDefault();
          return;
        }
        onClick(event, props as MenuItemProps);
      }
    : undefined;

  return (
    <StyledMenuItem
      as={asChild ? Slot : onClick || link || href || to ? "a" : "div"}
      className={cx(
        className,
        active && "is-active",
        disabled && "is-disabled",
        header && "is-header",
        iconOnly && "is-icon",
        position === "right" && "is-position-right",
        position === "left" && "is-position-left",
      )}
      style={style}
      $fitted={fitted}
      $colorValue={resolveItemColor(theme, { color, disabled, header, active })}
      to={to}
      href={href}
      role={!disabled && (to || href || onClick) ? "button" : undefined}
      tabIndex={!disabled && (to || href || onClick) ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={handleClickForKeyboard(
        handleClick as (e: React.UIEvent) => void,
      )}
      {...(rest as object)}
    >
      {asChild && slotChild
        ? cloneElement(slotChild, undefined, renderedIcon, body)
        : [renderedIcon, body]}
    </StyledMenuItem>
  );
};

export const MenuItem = styled(MenuItemComponent)``;
